import changelogRaw from '/CHANGELOG.md?raw'

export type ChangelogVersion = {
  version: string
  date: string
  content: string
  /** false when the entry has `<!-- popup: false -->`: listed on the page, no popup. */
  popup: boolean
}

const POPUP_OFF_MARKER = /<!--\s*popup:\s*false\s*-->/i

export const parseVersions = (raw: string): ChangelogVersion[] => {
  const versions: ChangelogVersion[] = []
  const blocks = raw.split(/^## v/m).slice(1)

  for (const block of blocks) {
    const firstLine = block.split('\n')[0]
    const match = firstLine.match(/^(\d+\.\d+\.\d+)\s*\((\d{4}-\d{2}-\d{2})\)/)
    if (!match) continue

    const body = block.split('\n').slice(1).join('\n')

    versions.push({
      version: match[1],
      date: match[2],
      content: body.replace(POPUP_OFF_MARKER, '').trim(),
      popup: !POPUP_OFF_MARKER.test(body),
    })
  }

  return versions
}

/** Compares "x.y.z" numerically. Returns <0, 0 or >0. Unparsable parts count as 0. */
export const compareVersions = (a: string, b: string): number => {
  const pa = a.split('.').map((n) => Number.parseInt(n, 10) || 0)
  const pb = b.split('.').map((n) => Number.parseInt(n, 10) || 0)
  for (let i = 0; i < 3; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (diff !== 0) return diff
  }
  return 0
}

/** The higher of two versions; null/undefined/empty count as "nothing seen". */
export const maxVersion = (
  a: string | null | undefined,
  b: string | null | undefined,
): string | null => {
  if (!a) return b || null
  if (!b) return a
  return compareVersions(a, b) >= 0 ? a : b
}

/** Entries newer than lastSeen, newest first. lastSeen null = all entries. */
export const getUnseenVersions = (
  all: ChangelogVersion[],
  lastSeen: string | null,
): ChangelogVersion[] =>
  lastSeen ? all.filter((v) => compareVersions(v.version, lastSeen) > 0) : all

export const versions = parseVersions(changelogRaw)

export const getFullChangelog = (): string => changelogRaw

export const getLatestVersion = (): ChangelogVersion | undefined => versions[0]
